import {usePlantConfig} from '@domain/shifthandover'
import {
  UnsupportedChecklistKindErrorPage,
  hrocChecklistsFeatureTranslations
} from '@features/hroc-checklists'
import {InstructionsPageLastVisitedProvider} from '@features/instructions-and-documents'
import {eventsAffiliatesRoutes} from '@features/shift-events-affiliates'
import {stoppageAnalysisFeatureTranslations} from '@features/stoppage-analysis'
import {User} from '@hconnect/apiclient/src/response/User'
import {LegalPage} from '@hconnect/common/components/legal/LegalPage'
import {TermsAndConditions} from '@hconnect/common/components/legal/TermsAndConditions'
import {Env, WhiteList} from '@hconnect/common/hproduce'
import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
import {useLoginContext} from '@hconnect/login'
import {Shell, ThemeMode, ThemeSettingsProvider} from '@hconnect/uikit/src/lib2'
import {UpmPlantProvider, useUpmPlant} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {
  Routes,
  Route,
  createBrowserRouter,
  RouterProvider,
  Link as RouterLink,
  generatePath,
  Navigate,
  useRoutes
} from 'react-router'

import {HLog} from './common/utils/logging'
import {ShiftErrorBoundary} from './components/common'
import {PageLoadingSpinner} from './components/common/PageLoadingSpinner'
import {GlobalErrorBoundary} from './containers/common/GlobalErrorBoundary'
import {FooterContent} from './containers/footer/FooterContent'
import {HeaderContent} from './containers/header/HeaderContent'
import {CockpitToursProvider} from './hooks/useCockpitTours'
import {trackAnalyticsEvent} from './hooks/useTrackAnalyticsEvents'
import {CockpitEventsPage} from './pages/CockpitEventsPage'
import {DocumentsPage} from './pages/DocumentsPage'
import {LabQualityResultPage} from './pages/LabQualityResultPage'
import {ManageShiftPage} from './pages/ManageShiftsPage'
import {PerformancePage} from './pages/PerformancePage'
import {RecurringTasksPage} from './pages/RecurringTasksPage'
import {ShiftSummaryPage} from './pages/ShiftSummaryPage'
import {SimpleErrorPage} from './pages/SimpleErrorPage'
import {StoppageAnalysisPage} from './pages/StoppageAnalysisPage'
import {TaskChecklistPage} from './pages/TaskChecklistPage'
import {TasksPage} from './pages/TasksPage'
import {
  routeToManageShifts,
  routeToTasks,
  routeToEvents,
  routeToSummary,
  routeToRecurringTasks,
  routeToDocuments,
  routeToTermsConditions,
  routeToPrivacyPolicy,
  routeToPerformance,
  routeToLabQualityResult,
  routeToEventsAffiliates,
  routeToEventChecklist,
  routeToEventChecklistsUnsupportedKind,
  routeToTasksChecklist,
  routeToTasksChecklistUnsupportedKind,
  routeToStoppageAnalysis
} from './routes'

const EventsAffiliatesRoutes: FC = () => {
  // Legacy cockpit has no theme switcher; pin the light surface so the feature's
  // useFundamentalMuiTheme() has the ThemeSettingsProvider context it requires.
  return (
    <ThemeSettingsProvider initialThemeMode={ThemeMode.Light}>
      {useRoutes([eventsAffiliatesRoutes])}
    </ThemeSettingsProvider>
  )
}

type InitParams = {
  user: User
  whiteList: WhiteList
  plantId: string
  hasFullCockpitAccess: boolean
}

type FeatureFlagsEnabled = {
  isTermsAndConditionsEnabled: boolean | undefined
  isLabQualityResultPageEnabled: boolean | undefined
}

export const App = ({user, whiteList, plantId, hasFullCockpitAccess}: InitParams) =>
  hasFullCockpitAccess ? (
    <FullCockpitApp user={user} whiteList={whiteList} plantId={plantId} />
  ) : (
    <AffiliateOnlyApp whiteList={whiteList} />
  )

const AffiliateOnlyApp: FC<{
  whiteList: WhiteList
}> = ({whiteList}) => {
  const {t} = useTranslation()

  const router = createBrowserRouter([
    {
      path: routeToEventsAffiliates,
      element: (
        <GlobalErrorBoundary whiteList={whiteList}>
          <EventsAffiliatesRoutes />
        </GlobalErrorBoundary>
      )
    },
    {
      path: '*',
      element: (
        <SimpleErrorPage
          message={t('error.missingPermission')}
          withRedirectButton={true}
          env={process.env.REACT_APP_STAGE as Env}
        />
      )
    }
  ])

  return <RouterProvider router={router} />
}

const FullCockpitApp: FC<{
  user: User
  whiteList: WhiteList
  plantId: string
}> = ({user, whiteList, plantId}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {logout} = useLoginContext()
  // Resolves the SAP plantId → upmId so feature code (e.g. instructions & documents) can read the
  // plant from UpmPlantProvider context. AppWrapper only renders once UPM plants are loaded.
  const {upmId} = useUpmPlant({plantId})

  const {data: config, isLoading, error} = usePlantConfig({plantId, language})

  const noShifts = !config?.shifts || config.shifts.length === 0
  const isTermsAndConditionsEnabled = useCommonFeatureFlag(FeatureFlagKey.TermsAndConditions)
  const isLabQualityResultPageEnabled = useCommonFeatureFlag(FeatureFlagKey.LabQualityResultPage, {
    plantId
  })

  const featureFlags: FeatureFlagsEnabled = useMemo(
    () => ({
      isTermsAndConditionsEnabled,
      isLabQualityResultPageEnabled
    }),
    [isTermsAndConditionsEnabled, isLabQualityResultPageEnabled]
  )

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: routeToEventsAffiliates,
          element: (
            <GlobalErrorBoundary whiteList={whiteList}>
              <EventsAffiliatesRoutes />
            </GlobalErrorBoundary>
          )
        },
        {
          path: '*',
          element: (
            <CockpitToursProvider plantId={plantId}>
              <UpmPlantProvider plantUpmId={upmId}>
                <InstructionsPageLastVisitedProvider>
                  <GlobalErrorBoundary whiteList={whiteList}>
                    <Shell
                      isResponsive
                      header={
                        <HeaderContent
                          onLogout={() => void logout()}
                          whiteList={whiteList}
                          plantId={plantId}
                        />
                      }
                      boxed={false}
                      footer={<FooterContent plantId={plantId} />}
                    >
                      <AppShellContent
                        featureFlags={featureFlags}
                        user={user}
                        plantId={plantId}
                        noShifts={noShifts}
                      />
                    </Shell>
                  </GlobalErrorBoundary>
                </InstructionsPageLastVisitedProvider>
              </UpmPlantProvider>
            </CockpitToursProvider>
          )
        }
      ]),
    [whiteList, plantId, upmId, logout, user, noShifts, featureFlags]
  )

  if (isLoading || !upmId) return <PageLoadingSpinner />

  if (error || !config) {
    HLog('cockpit')
      .tags({
        env: process.env.REACT_APP_STAGE ?? 'unknown',
        user: user.id,
        roll: user.hasRoles.join()
      })
      .warning('fail to load the app config')

    trackAnalyticsEvent('onErrorPageTracking', {
      plantId: plantId,
      userId: user.id,
      errorMessage: `We are very sorry, we couldn't start the application with the provided plantId: ${plantId}`
    })
    return (
      <SimpleErrorPage
        message={`${t('error.failToLoadAppConfig')}${plantId}`}
        withRedirectButton={true}
        env={process.env.REACT_APP_STAGE as Env}
      />
    )
  }

  return <RouterProvider router={router} />
}

const AppShellContent = ({
  featureFlags,
  user,
  plantId,
  noShifts
}: {
  featureFlags: FeatureFlagsEnabled
  user: User
  plantId: string
  noShifts: boolean
}) => {
  return (
    <>
      {featureFlags.isTermsAndConditionsEnabled && <TermsAndConditions user={user} />}
      <Routes>
        <Route path={routeToManageShifts} element={<ManageShiftPage />} />
        {plantId && noShifts && (
          <Route
            path="*"
            element={<Navigate to={generatePath(routeToManageShifts, {plantId})} />}
          />
        )}
        <Route
          path={routeToEvents}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <CockpitEventsPage />
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToEventChecklist}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
                <TaskChecklistPage />
              </FeatureTranslationsLoader>
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToEventChecklistsUnsupportedKind}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
                <UnsupportedChecklistKindErrorPage />
              </FeatureTranslationsLoader>
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToTasks}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <TasksPage />
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToTasksChecklist}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
                <TaskChecklistPage />
              </FeatureTranslationsLoader>
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToTasksChecklistUnsupportedKind}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <FeatureTranslationsLoader translations={hrocChecklistsFeatureTranslations}>
                <UnsupportedChecklistKindErrorPage />
              </FeatureTranslationsLoader>
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToSummary}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <ShiftSummaryPage />
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToStoppageAnalysis}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <FeatureTranslationsLoader translations={stoppageAnalysisFeatureTranslations}>
                <StoppageAnalysisPage />
              </FeatureTranslationsLoader>
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToPerformance}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <PerformancePage />
            </ShiftErrorBoundary>
          }
        />
        <Route
          path={routeToRecurringTasks}
          element={
            <ShiftErrorBoundary plantId={plantId}>
              <RecurringTasksPage />
            </ShiftErrorBoundary>
          }
        />
        <Route path={routeToDocuments} element={<DocumentsPage />} />
        <Route
          path={routeToLabQualityResult}
          element={
            featureFlags.isLabQualityResultPageEnabled ? (
              <ShiftErrorBoundary plantId={plantId}>
                <LabQualityResultPage />
              </ShiftErrorBoundary>
            ) : (
              <Navigate to={generatePath(routeToEvents, {plantId})} replace />
            )
          }
        />
        {featureFlags.isTermsAndConditionsEnabled && (
          <Route
            path={routeToTermsConditions}
            element={<LegalPage routerLinkComponent={RouterLink} user={user} type="tc" />}
          />
        )}
        {featureFlags.isTermsAndConditionsEnabled && (
          <Route
            path={routeToPrivacyPolicy}
            element={<LegalPage routerLinkComponent={RouterLink} user={user} type="dpp" />}
          />
        )}
        <Route path="*" element={<Navigate to={generatePath(routeToEvents, {plantId})} />} />
      </Routes>
    </>
  )
}
